import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import InputMask from "react-input-mask";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import fp from "lodash/fp";
import moment from "moment";
import { Formik } from "formik";
import * as yup from "yup";

import Categories from "../categories/Categories";
import WithSelect from "../../components/select";
import { RangePicker } from "../../components/datepicker/DatePicker";
import * as assetsActions from "../../modules/assets";
import * as fileMetaAttributionsActions from "../../modules/fileMetaAttributions";

import { ASSET_LOCKED_STATE, CONSTANTS, SEARCH_FILTER_TYPE, SPACE_SCOPE, UNCATEGORIZED_ID } from "../../constants";
import compareVersions, {
    VERSION_CM_11209_ADD_CUSTOM_METADATA,
    VERSION_CM_12923_LIST_SEARCHBAR_FILTER,
    VERSION_CM_13540_ASSET_SEARCH_BAR_V2,
    VERSION_CM_14830_SEPARATION_OF_OWNER_AND_CREATOR,
    VERSION_CM_15198_ASSET_SEARCHBAR_LOCKED_STATE,
    VERSION_CM_3963_ADD_ASSET_TYPE,
    VERSION_CM_4923_FILE_META_SEARCH,
    VERSION_CM_6575_ASSET_CATEGORY_GROUP_SEARCH,
    VERSION_DAM_275_ASSET_SEARCH_INDEX_STATUS,
} from "../../cores/version";
import VersionedComponent from "../../components/VersionedComponent";

import i18n from "../../lib/i18n";
import SearchQueryParamsContext from "../../components/params/SearchQueryParamsContext";
import {
    Badge,
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Popover,
    Select,
    Stack,
    TextField,
    Tooltip,
} from "@mzc-pdc/ui";
import { IconClose, IconOpenInNew, IconResetSmall, IconSchema, IconSearch, IconTune } from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import { alpha } from "@mui/material";
import { useTranslation } from "react-i18next";
import SelectCustomMetadata from "@features/dialog/custom-metadata/dialog-select-custom-metadata";
import useDialog from "@hooks/useDialog";
import MetadataFieldEditMode from "@features/metadata-fields/metadata-field-edit-mode";
import useSettings from "@hooks/useSettings";
import PanelPropertyTitle from "@components/view/components/panel-property-title";
import useCustomContent from "@hooks/useCustomContent";
import { getContentSchemaData } from "@modules/content-schema";
import dayjs from "dayjs";
import ResponsiveSearchFilters from "@components_v2/search-filters/responsive-search-filters";
import AttributionInputArea from "@components/inputArea/AttributionInputArea";
import FolderInputDialog from "@features/folder-input/folder-input-dialog";
import { ChipOwnersInput, ChipUsersInputV2 } from "@features/chip-popper-components/chip-users-input";
import { Autocomplete, AutocompleteMultipleProjects } from "@components_v2/autocomplete";
import TagsInput from "@features/tags-input/tags-input";
import { BasicDatePicker } from "@features/datepicker/datepicker";
import { convertToUTCDateTime } from "@utils/convertToUTCDateTime";
import Typography from "@components_v2/typography/typography";
import SelectCategoryGroupPopper from "@features/categories-input/select-category-group-popper";

// TODO: 각 항목을 유동적으로 입력할수 있도록 수정
const AssetSearchBar = (props) => {
    const {
        domain,
        initialValues,
        validationSchema: validationSchemaProp,
        isQuickSearch,
        optionValues,
        onClickSubmit,
        onClickReset,
        onChangeQuickSearch,
        isSearchEngine,
        aiSearchEnabled,
        projectId,
        hiddenSearchBarParams = [],
        hiddenSearchFilterParams = [],
        currentCategoryGroup,
        aiModeSlot,
    } = props;
    const searchQueryParamsContext = useContext(SearchQueryParamsContext);
    const { t } = useTranslation();
    const { dialog, openDialog, closeDialog } = useDialog();
    const { domainStatus } = useSettings({ type: "DomainStatus" });
    const { getCustomMetadataByContentSchema } = useCustomContent();
    const dispatch = useDispatch();
    const boundedFileMetaAttributionActions = bindActionCreators(fileMetaAttributionsActions, dispatch);
    const AssetsActions = bindActionCreators(assetsActions, dispatch);

    const stage = useSelector((state) => state.stage);
    const project = useSelector((state) => state.project);
    const assets = useSelector((state) => state.assets);
    const fileMetaAttributions = useSelector((state) => state.fileMetaAttributions);

    const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);
    const [fileMetaKeyOptions, setFileMetaKeyOptions] = useState([]);
    const [assetTypesList, setAssetTypesList] = useState([]);
    const [paramsForCount, setParamsForCount] = useState([]);
    const [metadataTemplate, setMetadataTemplate] = useState();
    const [customMetadata, setCustomMetadata] = useState(null);
    const [extensionList, setExtensionList] = useState([]);
    const [projectList, setProjectList] = useState([]);
    const hashTagInputParent = useRef();
    const paramRef = useRef(null);
    const ref = useRef();
    const MAX_CONTENT_FIELDS = 5;

    const [validationSchema, setValidationSchema] = useState(
        validationSchemaProp ||
            yup.object().shape({
                name: yup.string().default(""),
                id: yup.string().default(""),
                types: yup.array().nullable(),
                mediaTypes: yup.array().nullable(),
                indexDocStatus: yup.array().nullable(),
                ingestType: yup.string().nullable().default(""),
                jobId: yup.string().default(""),
                videoId: yup.string().default(""),
                status: yup.string().default(""),
                creator: yup.string().nullable().default(""),
                owner: yup.object().shape({ id: yup.string(), name: yup.string() }),
                keyword: yup.string().default(""),
                description: yup.string().default(""),
                categoryIds: yup.array().nullable(),
                // .of(yup.mixed().oneOf([yup.string(), yup.number(), yup.object()]))
                // .nullable(),
                tags: yup.array().of(yup.string()).default([]),
                endAt: yup.string().nullable().default(null),
                startAt: yup.string().nullable().default(null),
            }),
    );

    const onFormikSubmit = useCallback(
        (values, actions) => {
            const values_ = { ...values };
            if (onClickSubmit) onClickSubmit(values_);
            actions.setSubmitting(false);
        },
        [onClickSubmit],
    );

    const checkAdvancedSearch = (key, value, params = []) => {
        try {
            const initialParams = [
                "withAutoArchiveDate",
                "isNotCategoryIds",
                "selectedCategoryId",
                "activeView",
                "hierarchyType",
                "virtualDriveId",
                "virtualFolderId",
                "keyword",
                "projectId",
                "isQuickSearch",
                "limit",
                "offset",
                "stageId",
                "isCardView",
                "size",
                "createdByName",
                "enableFolderView",
                "enableCategoryView",
                "defaultCategoryGroupId",
                "selectedContentSchemaId",
                "categoryGroup",
                "isNotIngestType",
            ];
            const updateParams = (paramsForCount, key, value) => {
                let _paramsForCount = paramsForCount;
                const validationValue =
                    typeof value === "string" ? value.length > 0 : Array.isArray(value) ? value.length > 0 : !!value;
                // 추가
                if (validationValue) {
                    if (!_paramsForCount.some((p) => p === key)) return [..._paramsForCount, key];
                }
                // 삭제
                if (!validationValue) {
                    if (key === "contentSchema") {
                        _paramsForCount = _paramsForCount.filter((p) => p.indexOf("contentFields") < 0);
                    }
                    return _paramsForCount.filter((p) => p !== key);
                }
                return _paramsForCount;
            };

            const isSearchParams = !initialParams.some((el) => el === key);

            let _paramsForCount = params;
            if (isSearchParams) {
                _paramsForCount = updateParams(_paramsForCount, key, value);
            }
            return _paramsForCount;
        } catch (e) {
            console.log(e);
        }
    };

    useEffect(() => {
        const parsedOptions = [];
        if (fileMetaAttributions && fileMetaAttributions.keys && fileMetaAttributions.keys.items) {
            const items = fileMetaAttributions.keys.items;
            const length = items.length;
            for (let i = 0; i < length; i++) {
                const item = items[i];
                const key = item.key;
                parsedOptions.push({
                    label: key,
                    value: key,
                    sampleValue: item.sampleValue,
                });
            }
        }
        setFileMetaKeyOptions(parsedOptions);
    }, [fileMetaAttributions]);

    const renameContentFieldKey = (fieldId) => {
        // NOTE: contentSchema 삭제 시 contentFields를 일괄 삭제하기 위해 key를 contentFields-{{id}}로 변경
        if (!fieldId) return null;
        return `contentFields-${fieldId}`;
    };

    const handleChangeValue = async (key, value) => {
        paramRef.current.setFieldValue(key, value);
    };

    const handleChangeValues = async (params) => {
        // NOTE: key, value 형태의 1개 이상의 값이 들어오는 경우 ex) startAt, endAt
        try {
            for (const [key, value] of Object.entries(params)) {
                await paramRef.current?.setFieldValue(key, value);
            }
        } catch (error) {
            console.error("Error updating filter values:", error);
        }
    };

    const handleChangeFilterValue = async (params) => {
        try {
            for (const [key, value] of Object.entries(params)) {
                if (key === "contentSchema") {
                    initContentSchemaMetadata(value);
                    if (!value) await paramRef.current?.setFieldValue("contentFields", undefined);
                }
                await paramRef.current?.setFieldValue(key, value);
            }
            paramRef.current?.handleSubmit();
        } catch (error) {
            console.error("Error updating filter values:", error);
        }
    };

    const handleChangeContentSchemaValue = (customMetadata) => {
        if (!customMetadata) {
            handleChangeValue("contentSchema", null);
        } else {
            if (customMetadata.id !== metadataTemplate?.id) {
                handleChangeValue("contentSchema", {
                    id: customMetadata.id,
                    value: customMetadata.id,
                    label: customMetadata.name,
                });
            }
        }
        initContentSchemaMetadata(customMetadata);
    };

    const initContentSchemaMetadata = async (customMetadata) => {
        if (!customMetadata) {
            setCustomMetadata(null);
        } else {
            let fields = [];
            if (
                !customMetadata?.fields?.length ||
                customMetadata?.fields?.filter((el) => el.value && (!el.type || !el.subtitle)).length > 0
            ) {
                const response = await dispatch(getContentSchemaData(customMetadata.id, project.id));
                const parsedCustomMetadata = getCustomMetadataByContentSchema(response);
                fields = parsedCustomMetadata?.fields?.map((f) => {
                    const value = customMetadata?.fields?.find((el) => el.id === f.id)?.value ?? null;
                    return { ...f, value };
                });
            } else {
                fields = customMetadata.fields.map((f) => ({ ...f, value: null }));
            }
            if (!customMetadata?.label) customMetadata.label = customMetadata.name;
            setCustomMetadata({ ...customMetadata, fields });
        }
        closeDialog();
    };

    const handleClearCheckAll = () => {
        handleChangeValue("contentSchema", null);
        initContentSchemaMetadata(null);
    };

    const onChangeAttributionValue = (id, value) => {
        const _attributions = paramRef.current.values.attributions.map((el) => (el.id === id ? { ...el, value } : el));
        handleChangeValue("attributions", _attributions);
    };

    const onChangeAttributionKey = (id, key) => {
        const _attributions = paramRef.current.values.attributions.map((el) => (el.id === id ? { ...el, key } : el));
        handleChangeValue("attributions", _attributions);
    };

    const onClickAddAttribution = (id, value) => {
        handleChangeValue("attributions", (value || []).concat([{ id, key: "", value: "" }]));
    };
    const onClickRemoveAttribution = (id) => {
        handleChangeValue(
            "attributions",
            paramRef.current.values.attributions.filter((a) => a.id !== id),
        );
    };

    const handleChangeMetadata = useCallback(
        (id, value) => {
            const newCustomFields = customMetadata.fields.map((field) =>
                field.id === id ? { ...field, value } : field,
            );

            let customFields = newCustomFields.map((el) => ({ fieldId: el.id, value: el.value, fieldType: el.type }));
            setCustomMetadata({ ...customMetadata, fields: newCustomFields });
            handleChangeValue("contentFields", customFields);
        },
        [customMetadata],
    );

    const filteredCustomMetadata = useMemo(() => {
        return customMetadata?.fields?.filter((el) => !["MULTI_SELECT", "RANGE_DATE_PICKER"].includes(el.type)) ?? [];
    }, [customMetadata]);

    const initParamsForCount = useCallback(async () => {
        try {
            let updatedParams = [];
            for (const [key, value] of Object.entries(initialValues)) {
                if (key === "contentFields") {
                    const addedFields =
                        value.filter(({ value }) => value).map(({ fieldId }) => renameContentFieldKey(fieldId)) || [];
                    updatedParams = [...updatedParams, ...addedFields];
                } else {
                    updatedParams = await checkAdvancedSearch(key, value, updatedParams);
                }
            }
            setParamsForCount(updatedParams);
        } catch (e) {
            console.error(e);
        }
    }, [paramsForCount, initialValues]);

    useEffect(() => {
        const assetTypes = AssetsActions.getAssetTypeSelectList(
            (searchQueryParamsContext.params.mediaTypes || []).map((mt) => mt.value),
        ).filter((el) => el.value);
        if (assetTypesList.length !== assetTypes.length) setAssetTypesList(assetTypes);
    }, [assets.assetTypes.data]);

    useEffect(() => {
        if (assets.assetExtensions.data.length + 1 !== extensionList.length) {
            setExtensionList((assets.assetExtensions && assets.assetExtensions.data) || []);
        }
    }, [assets.assetExtensions.data]);

    const initCustomMetadata = async () => {
        if (initialValues?.contentSchema?.id) {
            let customMetadata = { ...initialValues.contentSchema };
            if (initialValues?.contentFields) {
                const fields = initialValues?.contentFields;
                const newCustomFields = fields.map((field) => {
                    const { fieldId, value } = field;
                    return { id: fieldId, value };
                });

                customMetadata = { ...customMetadata, fields: newCustomFields };
            }
            initContentSchemaMetadata(customMetadata);
        } else {
            initContentSchemaMetadata(null);
        }
    };
    useEffect(() => {
        // TODO: [GET] /file-meta-keys 으로 변경
        if (compareVersions(stage.version, VERSION_CM_4923_FILE_META_SEARCH) >= 0) {
            boundedFileMetaAttributionActions.getKeys();
        }
        if (compareVersions(VERSION_CM_3963_ADD_ASSET_TYPE, stage.version) <= 0) {
            AssetsActions.listAssetTypes();
            AssetsActions.listAssetExtensions();
        }
        initParamsForCount();
        initCustomMetadata();
    }, []);

    useEffect(() => {
        initParamsForCount();
        if (initialValues.contentSchema?.id !== customMetadata?.id) initCustomMetadata();
    }, [initialValues]);

    const onHandleClickReset = async () => {
        await onClickReset();
    };

    const searchFilterCount = useMemo(() => {
        // 날짜 범위 관련 파라미터들
        const dateParams = ["startAt", "endAt"];
        // 기간 범위 관련 파라미터들
        const durationParams = ["beginDuration", "endDuration"];
        // 폴더 파라미터들
        const folderParams = ["folderName", "folderPath"];

        let count = 0;
        let hasDateRange = false;
        let hasDurationRange = false;
        let hasFolder = false;

        paramsForCount.forEach((param) => {
            // 날짜 범위 파라미터인 경우
            if (dateParams.includes(param)) {
                if (!hasDateRange) {
                    count++;
                    hasDateRange = true;
                }
            }
            // 기간 범위 파라미터인 경우
            else if (durationParams.includes(param)) {
                if (!hasDurationRange) {
                    count++;
                    hasDurationRange = true;
                }
            }
            // 폴더 파라미터인 경우
            else if (folderParams.includes(param)) {
                if (!hasFolder) {
                    count++;
                    hasFolder = true;
                }
            }
            // 그 외 일반 파라미터
            else {
                count++;
            }
        });

        return count;
    }, [paramsForCount]);

    const getModifiedOptions = useCallback((options, value) => {
        return options?.map((option) => ({
            ...option,
            disabled:
                value?.length >= MAX_CONTENT_FIELDS &&
                !value?.find((selectedOption) => selectedOption.value === option.value),
        }));
    }, []);

    const getMaxCount = useCallback((value) => {
        return value.length > 0 ? (
            <>
                {" "}
                (
                <Typography component="span" color={theme.palette.primary.main} display="inline">
                    {value.length}
                </Typography>
                /{MAX_CONTENT_FIELDS})
            </>
        ) : null;
    }, []);

    const filteredIngestTypes =
        initialValues.isNotIngestType === "TRANSCODED"
            ? CONSTANTS("ASSET_INGEST_TYPES").filter((t) => t.value !== "TRANSCODED")
            : CONSTANTS("ASSET_INGEST_TYPES");

    return (
        <>
            {dialog === "selectCustomMetadata" && (
                <SelectCustomMetadata
                    domain={"ASSET"}
                    projectId={projectId ?? project.id}
                    defaultTemplate={metadataTemplate}
                    onClickClose={() => closeDialog()}
                    onClickSubmit={handleChangeContentSchemaValue}
                />
            )}
            <Formik
                innerRef={paramRef}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values, actions) => {
                    const newValues = { ...values, isQuickSearch: false, offset: 0 };
                    if (!isQuickSearch) {
                        newValues.keyword = values.name;
                        if (onChangeQuickSearch) {
                            onChangeQuickSearch();
                        }
                    } else {
                        newValues.name = values.keyword;
                    }
                    onFormikSubmit(newValues, actions);
                }}
                enableReinitialize={true}
            >
                {({ values, handleSubmit, resetForm }) => (
                    <form
                        onSubmit={(e) => {
                            // console.log("?", values);f
                            e.preventDefault();
                            handleSubmit(e);
                        }}
                    >
                        <Grid container flexDirection={"row"} alignItems={"center"} gap={2}>
                            <Grid item sx={{ maxWidth: aiModeSlot ? "360px" : "320px", width: "100%" }}>
                                <TextField
                                    className={"area-search"}
                                    ref={ref}
                                    type={"search"}
                                    variant={"filled"}
                                    fullWidth
                                    sx={{
                                        ".MuiFilledInput-root": {
                                            backgroundColor: alpha(theme.palette.greySecondary[50], 0.5),
                                        },
                                        [theme.breakpoints.down("xl")]: {
                                            width: `100%`,
                                        },
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <IconSearch size={16} color={"greySecondary"} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Tooltip title={i18n.t(`common::label::${"Advanced search"}`)}>
                                                    <IconButton
                                                        circled
                                                        size={"medium"}
                                                        onClick={(e) => {
                                                            if (onChangeQuickSearch) onChangeQuickSearch();
                                                        }}
                                                    >
                                                        <Badge badgeContent={searchFilterCount} color="primary">
                                                            <IconTune
                                                                size={20}
                                                                htmlColor={theme.palette.greySecondary.main}
                                                            />
                                                        </Badge>
                                                    </IconButton>
                                                </Tooltip>
                                                {aiModeSlot}
                                            </InputAdornment>
                                        ),
                                    }}
                                    placeholder={i18n.t(`common::label::Search assets`)}
                                    value={values.keyword || values.q || ""}
                                    onChange={(e) => handleChangeValue("keyword", e.target.value)}
                                />

                                <Popover
                                    anchorEl={ref.current}
                                    open={!isQuickSearch}
                                    onClose={() => {
                                        if (onChangeQuickSearch) onChangeQuickSearch();
                                    }}
                                    anchorOrigin={{
                                        vertical: "bottom",
                                        horizontal: "left",
                                    }}
                                    transformOrigin={{
                                        vertical: "top",
                                        horizontal: "left",
                                    }}
                                    sx={{
                                        ".MuiPaper-root": {
                                            padding: 3,
                                            width: ref.current ? "756px" : "100%",
                                            boxShadow: `0 3px 6px 0 rgba(0, 0, 0, 0.16)`,
                                            border: `1px solid ${theme.palette.secondary[600]}`,
                                            boxSizing: `border-box`,
                                        },
                                    }}
                                >
                                    <Grid container>
                                        <Grid item xs>
                                            <Box
                                                sx={{
                                                    display: `flex`,
                                                    flexDirection: `column`,
                                                    maxHeight: `70vh`,
                                                    overflowY: `auto`,
                                                    paddingRight: 2.5,
                                                    // 검색 조건 행(라벨 | 입력) 공통 좌우 간격
                                                    "& > .MuiStack-root > .MuiGrid-container": { columnGap: 2.5 },
                                                }}
                                                gap={2}
                                            >
                                                {isSearchEngine && !projectId && (
                                                    <Stack>
                                                        <Grid container alignItems={"center"}>
                                                            <Grid item xs={3}>
                                                                <Typography variant={"body1_400"}>
                                                                    {i18n.t(`common::label::Project`)}
                                                                    {getMaxCount(values.projectIds || [])}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs>
                                                                <AutocompleteMultipleProjects
                                                                    value={values[`projectIds`] ?? []}
                                                                    placeholder={
                                                                        (!values[`projectIds`] ||
                                                                            values[`projectIds`]?.length === 0) &&
                                                                        "All projects"
                                                                    }
                                                                    onInputChange={() =>
                                                                        handleChangeValue("projectIds", [])
                                                                    }
                                                                    options={getModifiedOptions(
                                                                        optionValues?.projectIds,
                                                                        values?.projectIds,
                                                                    )}
                                                                    getOptionDisabled={(option) =>
                                                                        values?.projectIds?.length >=
                                                                            MAX_CONTENT_FIELDS &&
                                                                        !values?.projectIds?.includes(option)
                                                                    }
                                                                    onChange={(projects) => {
                                                                        handleChangeValue(
                                                                            "projectIds",
                                                                            projects.map((el) => ({
                                                                                id: el.id,
                                                                                name: el.name,
                                                                                label: el.name,
                                                                                value: el.id,
                                                                            })),
                                                                        );
                                                                    }}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </Stack>
                                                )}
                                                <Stack>
                                                    <Grid container alignItems={"center"}>
                                                        <Grid item xs={3}>
                                                            <Typography variant={"body1_400"}>
                                                                {i18n.t(`common::label::Assets {{name}}`, {
                                                                    name: i18n.t(`common::label::Name`),
                                                                })}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs>
                                                            <input
                                                                type="text"
                                                                name="name"
                                                                placeholder={i18n.t(`common::label::Search`)}
                                                                className={"form-control"}
                                                                value={values.name || ""}
                                                                onChange={(e) =>
                                                                    handleChangeValue("name", e.target.value)
                                                                }
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Stack>
                                                <Stack>
                                                    <Grid container alignItems={"center"}>
                                                        <Grid item xs={3}>
                                                            <Typography variant={"body1_400"}>
                                                                {i18n.t(`common::label::Assets {{name}}`, {
                                                                    name: i18n.t(`common::label::ID`),
                                                                })}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs>
                                                            <input
                                                                type="text"
                                                                name="id"
                                                                placeholder={i18n.t(`common::label::Search`)}
                                                                className={"form-control"}
                                                                value={values.id}
                                                                onChange={(e) =>
                                                                    handleChangeValue("id", e.target.value)
                                                                }
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Stack>
                                                {!isSearchEngine && (
                                                    <Stack>
                                                        <Grid container alignItems={"center"}>
                                                            <Grid item xs={3}>
                                                                <Typography variant={"body1_400"}>
                                                                    {i18n.t(`common::label::Status`)}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs>
                                                                <Select
                                                                    fullWidth
                                                                    displayEmpty
                                                                    value={values.status}
                                                                    onChange={(e) => {
                                                                        handleChangeValue("status", e.target.value);
                                                                    }}
                                                                    renderValue={(selected) => {
                                                                        if (!selected) {
                                                                            return (
                                                                                <Typography
                                                                                    variant={"body1_400"}
                                                                                    color={
                                                                                        theme.palette.greySecondary[400]
                                                                                    }
                                                                                >
                                                                                    {t(
                                                                                        `common::label::All status`,
                                                                                        `All status`,
                                                                                    )}
                                                                                </Typography>
                                                                            );
                                                                        }

                                                                        const selectedLabel = CONSTANTS(
                                                                            "ACTIVE_STATUS",
                                                                        ).find(
                                                                            (option) => option.value === selected,
                                                                        )?.label;
                                                                        return selectedLabel;
                                                                    }}
                                                                >
                                                                    {CONSTANTS("ACTIVE_STATUS").map((option) => (
                                                                        <MenuItem
                                                                            value={option.value}
                                                                            key={option.value}
                                                                        >
                                                                            {option.label}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </Grid>
                                                        </Grid>
                                                    </Stack>
                                                )}
                                                {!isSearchEngine &&
                                                compareVersions(
                                                    stage.version,
                                                    VERSION_CM_14830_SEPARATION_OF_OWNER_AND_CREATOR,
                                                ) < 0 ? (
                                                    <Stack>
                                                        <Grid container alignItems={"center"}>
                                                            <Grid item xs={3}>
                                                                <Typography variant={"body1_400"}>
                                                                    {i18n.t(`common::label::Owner`)}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs>
                                                                <input
                                                                    type="text"
                                                                    name="creator"
                                                                    placeholder={i18n.t(`common::label::Search`)}
                                                                    className={"form-control"}
                                                                    value={values.creator ?? ""}
                                                                    onChange={(e) => {
                                                                        handleChangeValue("creator", e.target.value);
                                                                    }}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </Stack>
                                                ) : (
                                                    projectId && (
                                                        <Stack>
                                                            <Grid container alignItems={"center"}>
                                                                <Grid item xs={3}>
                                                                    <Typography variant={"body1_400"}>
                                                                        {i18n.t(`common::label::Owner`)}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs>
                                                                    {compareVersions(
                                                                        stage.version,
                                                                        VERSION_CM_14830_SEPARATION_OF_OWNER_AND_CREATOR,
                                                                    ) < 0 ? (
                                                                        <ChipUsersInputV2
                                                                            user={{
                                                                                id: values.createdBy,
                                                                                name: values.createdByName,
                                                                            }}
                                                                            placeholder={i18n.t(
                                                                                `common::label::Search`,
                                                                            )}
                                                                            onDeleteUser={() => {
                                                                                handleChangeValue("createdBy", null);
                                                                                handleChangeValue(
                                                                                    "createdByName",
                                                                                    null,
                                                                                );
                                                                            }}
                                                                            onAddUser={(user) => {
                                                                                handleChangeValue(
                                                                                    "createdByName",
                                                                                    user.name,
                                                                                );
                                                                                handleChangeValue("createdBy", user.id);
                                                                            }}
                                                                            isEdit={false}
                                                                            scope={SPACE_SCOPE.PROJECT}
                                                                            isPopperComponent={false}
                                                                        />
                                                                    ) : (
                                                                        <ChipOwnersInput
                                                                            user={values.owner || {}}
                                                                            placeholder={i18n.t(
                                                                                `common::label::Search`,
                                                                            )}
                                                                            onDeleteUser={() => {
                                                                                handleChangeValue("owner", {});
                                                                            }}
                                                                            onAddUser={(user) => {
                                                                                handleChangeValue("owner", {
                                                                                    id: user.id,
                                                                                    name: user.name,
                                                                                });
                                                                            }}
                                                                            isEdit={false}
                                                                            isPopperComponent={false}
                                                                        />
                                                                    )}
                                                                </Grid>
                                                            </Grid>
                                                        </Stack>
                                                    )
                                                )}
                                                <Stack>
                                                    <Grid container alignItems={"center"}>
                                                        <Grid item xs={3}>
                                                            <Typography variant={"body1_400"}>
                                                                {i18n.t(`common::label::Created`)}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs>
                                                            <RangePicker
                                                                startDate={
                                                                    !values.startAt ||
                                                                    dayjs(values.startAt)?.year() <= 1970
                                                                        ? undefined
                                                                        : moment(values.startAt)
                                                                }
                                                                endDate={
                                                                    !values.endAt || dayjs(values.endAt)?.year() >= 9999
                                                                        ? undefined
                                                                        : moment(values.endAt)
                                                                }
                                                                isOutsideRange={() => false}
                                                                changeDate={(date) => {
                                                                    if (date.startDate || date.endDate) {
                                                                        handleChangeValues({
                                                                            startAt: dayjs(
                                                                                date?.startDate ?? "1970-01-01",
                                                                            )
                                                                                .startOf("day")
                                                                                .toISOString(),
                                                                            endAt: dayjs(date?.endDate ?? "9999-12-31")
                                                                                .endOf("day")
                                                                                .toISOString(),
                                                                        });
                                                                    }
                                                                }}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Stack>
                                                {!hiddenSearchBarParams?.includes("location") &&
                                                    isSearchEngine &&
                                                    projectId && (
                                                        <Stack>
                                                            <Grid container alignItems={"center"}>
                                                                <Grid item xs={3}>
                                                                    <Typography variant={"body1_400"}>
                                                                        {i18n.t(`common::label::Location`)}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs>
                                                                    <FolderInputDialog
                                                                        fullWidth
                                                                        domain={"ASSET"}
                                                                        placeholder={"Select folder"}
                                                                        projectId={projectId}
                                                                        useDefaultFolder={false}
                                                                        value={{ folderName: values.folderName }}
                                                                        onSubmit={(v) => {
                                                                            handleChangeValues({
                                                                                folderName: v.folderName,
                                                                                folderPath: v.folderPath
                                                                                    ?.map((f) => f.name)
                                                                                    .join("/"),
                                                                            });
                                                                        }}
                                                                        // onHandleEditValue("virtualFolder", v)
                                                                        // onClose={() => onClickClose()}
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        </Stack>
                                                    )}
                                                {compareVersions(
                                                    stage.version,
                                                    VERSION_CM_15198_ASSET_SEARCHBAR_LOCKED_STATE,
                                                ) >= 0 &&
                                                    !isSearchEngine && (
                                                        <Stack>
                                                            <Grid container alignItems={"center"}>
                                                                <Grid item xs={3}>
                                                                    <Typography variant={"body1_400"}>
                                                                        {i18n.t(`common::label::Locked state`)}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs>
                                                                    <Select
                                                                        fullWidth
                                                                        displayEmpty
                                                                        value={values.isOwnerLocked}
                                                                        renderValue={(selected) => {
                                                                            if (!selected) {
                                                                                return (
                                                                                    <Typography
                                                                                        variant={"body1_400"}
                                                                                        color={
                                                                                            theme.palette
                                                                                                .greySecondary[400]
                                                                                        }
                                                                                    >
                                                                                        {t(`common::label::All`)}
                                                                                    </Typography>
                                                                                );
                                                                            }
                                                                            const selectedLabel =
                                                                                ASSET_LOCKED_STATE.find(
                                                                                    (option) =>
                                                                                        option.value === selected,
                                                                                )?.label;
                                                                            return t(`common::label::${selectedLabel}`);
                                                                        }}
                                                                        onChange={(e) =>
                                                                            handleChangeValues({
                                                                                isOwnerLocked: e.target.value,
                                                                            })
                                                                        }
                                                                    >
                                                                        {ASSET_LOCKED_STATE.map((el) => (
                                                                            <MenuItem
                                                                                key={el.value}
                                                                                value={el.value}
                                                                                sx={{ height: "40px" }}
                                                                            >
                                                                                <Typography>
                                                                                    {t(`common::label::${el.label}`)}
                                                                                </Typography>
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </Grid>
                                                            </Grid>
                                                        </Stack>
                                                    )}
                                                {/* 검색엔진을 통한 검색에는 indexDocStatus 검색조건을 제외하도록 백엔드에서 결정함 */}
                                                {!isSearchEngine &&
                                                    aiSearchEnabled &&
                                                    compareVersions(
                                                        stage.version,
                                                        VERSION_DAM_275_ASSET_SEARCH_INDEX_STATUS,
                                                    ) >= 0 && (
                                                        <Stack>
                                                            <Grid container alignItems={"center"}>
                                                                <Grid item xs={3}>
                                                                    <Typography variant={"body1_400"}>
                                                                        {i18n.t(
                                                                            `common::label::AI search indexing status`,
                                                                        )}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs>
                                                                    <Autocomplete
                                                                        id={"asset-search-bar-index-status"}
                                                                        multiple={true}
                                                                        disableClearable={false}
                                                                        disableCloseOnSelect={true}
                                                                        value={values?.indexDocStatus}
                                                                        options={CONSTANTS(
                                                                            "ASSET_SEARCH_INDEX_STATUSES",
                                                                        )}
                                                                        placeholder={t(
                                                                            `common::label::${"All status"}`,
                                                                        )}
                                                                        onClickOption={(_, options_) => {
                                                                            handleChangeValue(
                                                                                "indexDocStatus",
                                                                                options_,
                                                                            );
                                                                        }}
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        </Stack>
                                                    )}
                                                <Divider />
                                                <Stack>
                                                    <Grid container alignItems={"center"}>
                                                        <Grid item xs={3}>
                                                            <Typography variant={"body1_400"}>
                                                                {i18n.t(`common::label::Media type`)}
                                                                {getMaxCount(values.mediaTypes || [])}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs>
                                                            <Autocomplete
                                                                id={"asset-search-bar-media-type"}
                                                                multiple={true}
                                                                disableClearable={false}
                                                                disableCloseOnSelect={true}
                                                                value={values?.mediaTypes}
                                                                options={getModifiedOptions(
                                                                    CONSTANTS("ASSET_SEARCH_MEDIA_TYPES"),
                                                                    values?.mediaTypes,
                                                                )}
                                                                getOptionDisabled={(option) =>
                                                                    values?.mediaTypes?.length >= MAX_CONTENT_FIELDS &&
                                                                    !values?.mediaTypes?.includes(option)
                                                                }
                                                                placeholder={t(`common::label::${"All media type"}`)}
                                                                onClickOption={(_, options_) => {
                                                                    handleChangeValue("mediaTypes", options_);
                                                                }}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Stack>
                                                {!isSearchEngine && (
                                                    <Stack>
                                                        <Grid container alignItems={"center"}>
                                                            <Grid item xs={3}>
                                                                <Typography variant={"body1_400"}>
                                                                    {i18n.t(`common::label::Duration`)}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs>
                                                                <Grid container gap={0.5} alignItems={"center"}>
                                                                    <Grid item xs>
                                                                        <InputMask
                                                                            className={"input-group-time"}
                                                                            mask={"99:99:99"}
                                                                            placeholder={"hh:mm:ss"}
                                                                            value={values.beginDuration || ""}
                                                                            onChange={(e) => {
                                                                                handleChangeValue(
                                                                                    "beginDuration",
                                                                                    e.target.value,
                                                                                );
                                                                            }}
                                                                            style={{
                                                                                width: `100%`,
                                                                                height: 38,
                                                                                padding: `0 20px`,
                                                                                border: `1px solid ${theme.palette.greySecondary.light}`,
                                                                                borderRadius: 2,
                                                                                backgroundColor:
                                                                                    theme.palette.background.default,
                                                                                color: theme.palette.greySecondary.main,
                                                                            }}
                                                                        />
                                                                    </Grid>
                                                                    <Grid
                                                                        item
                                                                        sx={{
                                                                            backgroundColor:
                                                                                theme.palette.background.default,
                                                                            color: theme.palette.greySecondary.main,
                                                                        }}
                                                                    >
                                                                        ~
                                                                    </Grid>
                                                                    <Grid item xs>
                                                                        <InputMask
                                                                            className={"input-group-time"}
                                                                            mask={"99:99:99"}
                                                                            placeholder={"hh:mm:ss"}
                                                                            value={values.endDuration || ""}
                                                                            onChange={(e) => {
                                                                                handleChangeValue(
                                                                                    "endDuration",
                                                                                    e.target.value,
                                                                                );
                                                                            }}
                                                                            style={{
                                                                                width: `100%`,
                                                                                height: 38,
                                                                                padding: `0 20px`,
                                                                                border: `1px solid ${theme.palette.greySecondary.light}`,
                                                                                borderRadius: 2,
                                                                                backgroundColor:
                                                                                    theme.palette.background.default,
                                                                                color: theme.palette.greySecondary.main,
                                                                            }}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    </Stack>
                                                )}
                                                {compareVersions(VERSION_CM_3963_ADD_ASSET_TYPE, stage.version) <=
                                                    0 && (
                                                    <Stack>
                                                        <Grid container alignItems={"center"}>
                                                            <Grid item xs={3}>
                                                                <Typography variant={"body1_400"}>
                                                                    {i18n.t(`common::label::Type`)}
                                                                    {getMaxCount(values.types || [])}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs>
                                                                <Autocomplete
                                                                    id={"asset-search-bar-type"}
                                                                    multiple={true}
                                                                    disableClearable={false}
                                                                    disableCloseOnSelect={true}
                                                                    value={values?.types}
                                                                    options={getModifiedOptions(
                                                                        assetTypesList,
                                                                        values?.types,
                                                                    )}
                                                                    getOptionDisabled={(option) =>
                                                                        values?.types?.length >= MAX_CONTENT_FIELDS &&
                                                                        !values?.types?.includes(option)
                                                                    }
                                                                    placeholder={t(`common::label::All types`)}
                                                                    onClickOption={(_, options_) => {
                                                                        handleChangeValue("types", options_);
                                                                    }}
                                                                    loading={assets.assetTypes.pending}
                                                                    onOpen={() => {
                                                                        AssetsActions.listAssetTypes();
                                                                    }}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </Stack>
                                                )}
                                                {!hiddenSearchBarParams?.includes("categoryIds") && projectId && (
                                                    <Stack>
                                                        <Grid container alignItems={"center"}>
                                                            <Grid item xs={3}>
                                                                <Typography variant={"body1_400"}>
                                                                    {i18n.t(`common::label::Categories`)}
                                                                    {getMaxCount(values.categoryIds || [])}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs>
                                                                <Categories
                                                                    maxCount={MAX_CONTENT_FIELDS}
                                                                    hiddenCategoryGroupLabel={true}
                                                                    placeholder={i18n.t(
                                                                        `common::label::${"Select category"}`,
                                                                    )}
                                                                    type={CONSTANTS("CATEGORY_TYPES").ASSET}
                                                                    selectedValues={fp
                                                                        .getOr([], "categoryIds", values)
                                                                        ?.map((c) => {
                                                                            return {
                                                                                label: c.name,
                                                                                name: c.name,
                                                                                value: c.id,
                                                                                id: c.id,
                                                                            };
                                                                        })}
                                                                    onChange={(options) => {
                                                                        const ids = options.map((option) => ({
                                                                            id: option.id,
                                                                            name: option.name,
                                                                        }));

                                                                        handleChangeValue(
                                                                            "categoryIds",
                                                                            ids.length > 0 ? ids : undefined,
                                                                        );
                                                                    }}
                                                                    onChangeModalOpen={(v) => {
                                                                        setIsCategoriesModalOpen(v);
                                                                    }}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </Stack>
                                                )}
                                                {isSearchEngine && (
                                                    <Stack>
                                                        <Grid container alignItems={"center"}>
                                                            <Grid item xs={3}>
                                                                <Typography variant={"body1_400"}>
                                                                    {i18n.t(`common::label::Attributions`)}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs>
                                                                <AttributionInputArea
                                                                    validationErrorCheck={false}
                                                                    values={values.attributions || []}
                                                                    onChangeKey={(id, value) => {
                                                                        onChangeAttributionKey(id, value);
                                                                    }}
                                                                    onChangeValue={(id, e) => {
                                                                        onChangeAttributionValue(id, e);
                                                                    }}
                                                                    onClickAddAttribution={(id) => {
                                                                        onClickAddAttribution(id, values.attributions);
                                                                    }}
                                                                    onClickRemoveAttribution={(id) => {
                                                                        onClickRemoveAttribution(id);
                                                                    }}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </Stack>
                                                )}
                                                {compareVersions(VERSION_CM_3963_ADD_ASSET_TYPE, stage.version) <=
                                                    0 && (
                                                    <Stack>
                                                        <Grid container alignItems={"center"}>
                                                            <Grid item xs={3}>
                                                                <Typography variant={"body1_400"}>
                                                                    {i18n.t(`common::label::Extension`)}
                                                                    {getMaxCount(values.extensions || [])}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs>
                                                                <Autocomplete
                                                                    id={"asset-search-bar-extension"}
                                                                    multiple={true}
                                                                    disableClearable={false}
                                                                    disableCloseOnSelect={true}
                                                                    value={values?.extensions}
                                                                    options={getModifiedOptions(
                                                                        extensionList,
                                                                        values?.extensions,
                                                                    )}
                                                                    getOptionDisabled={(option) =>
                                                                        values?.extensions?.length >=
                                                                            MAX_CONTENT_FIELDS &&
                                                                        !values?.extensions?.includes(option)
                                                                    }
                                                                    placeholder={t(`common::label::All extensions`)}
                                                                    onClickOption={(_, options_) => {
                                                                        handleChangeValue("extensions", options_);
                                                                    }}
                                                                    loading={assets.assetExtensions.pending}
                                                                    onOpen={() => {
                                                                        AssetsActions.listAssetExtensions();
                                                                    }}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </Stack>
                                                )}
                                                <Stack>
                                                    <Grid container alignItems={"center"}>
                                                        <Grid item xs={3}>
                                                            <Typography variant={"body1_400"}>
                                                                {i18n.t(`common::label::Tags`)}
                                                                {getMaxCount(values.tags || [])}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs>
                                                            <TagsInput
                                                                maxCount={MAX_CONTENT_FIELDS}
                                                                tags={values.tags?.map((t) => ({ value: t }))}
                                                                onAddTag={({ value }) => {
                                                                    handleChangeValue(
                                                                        "tags",
                                                                        fp.getOr([], "tags", values).concat(value),
                                                                    );
                                                                }}
                                                                onDeleteTag={(removedIndex) => {
                                                                    handleChangeValue(
                                                                        "tags",
                                                                        fp
                                                                            .getOr([], "tags", values)
                                                                            .filter(
                                                                                (t, index) => index !== removedIndex,
                                                                            ),
                                                                    );
                                                                }}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Stack>
                                                {!isSearchEngine && (
                                                    <React.Fragment>
                                                        <Divider />
                                                        <Stack>
                                                            <Grid container alignItems={"center"}>
                                                                <Grid item xs={3}>
                                                                    <Typography variant={"body1_400"}>
                                                                        {i18n.t(`common::label::Job ID`)}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs>
                                                                    <input
                                                                        type="text"
                                                                        name="jobId"
                                                                        placeholder={i18n.t(`common::label::Search`)}
                                                                        className={"form-control"}
                                                                        value={values.jobId || ""}
                                                                        onChange={(e) =>
                                                                            handleChangeValue("jobId", e.target.value)
                                                                        }
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        </Stack>

                                                        <Stack>
                                                            <Grid container alignItems={"center"}>
                                                                <Grid item xs={3}>
                                                                    <Typography variant={"body1_400"}>
                                                                        {i18n.t(`common::label::Ingest type`)}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs>
                                                                    <Select
                                                                        fullWidth
                                                                        displayEmpty
                                                                        value={values.ingestType}
                                                                        onChange={(e) => {
                                                                            handleChangeValue(
                                                                                "ingestType",
                                                                                e.target.value,
                                                                            );
                                                                        }}
                                                                        renderValue={(selected) => {
                                                                            if (!selected) {
                                                                                return (
                                                                                    <Typography
                                                                                        variant={"body1_400"}
                                                                                        color={
                                                                                            theme.palette
                                                                                                .greySecondary[400]
                                                                                        }
                                                                                    >
                                                                                        {t(
                                                                                            `common::label::All ingest type`,
                                                                                            `All ingest type`,
                                                                                        )}
                                                                                    </Typography>
                                                                                );
                                                                            }

                                                                            const selectedLabel = CONSTANTS(
                                                                                "ASSET_INGEST_TYPES",
                                                                            ).find(
                                                                                (option) => option.value === selected,
                                                                            )?.label;
                                                                            return selectedLabel;
                                                                        }}
                                                                    >
                                                                        {filteredIngestTypes.map((option) => (
                                                                            <MenuItem
                                                                                value={option.value}
                                                                                key={option.value}
                                                                            >
                                                                                {option.label}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </Grid>
                                                            </Grid>
                                                        </Stack>
                                                    </React.Fragment>
                                                )}
                                                {/*Custom Metadata 영역*/}
                                                {!hiddenSearchBarParams?.includes("contentSchema") &&
                                                    !domainStatus.CUSTOM_CONTENTS_OFF &&
                                                    projectId &&
                                                    compareVersions(
                                                        stage.version,
                                                        VERSION_CM_11209_ADD_CUSTOM_METADATA,
                                                    ) >= 0 && (
                                                        <>
                                                            <Divider sx={{ mt: 2.5, mb: 3 }} />
                                                            <Stack>
                                                                <Typography
                                                                    variant={"body1_500"}
                                                                    color={theme.palette.greySecondary.main}
                                                                >
                                                                    {i18n.t(`common::label::Custom Metadata`)}
                                                                </Typography>
                                                                <Grid
                                                                    container
                                                                    flexDirection={"row"}
                                                                    mt={1.5}
                                                                    alignItems={"center"}
                                                                    columnGap={0}
                                                                >
                                                                    <Grid item flex={1}>
                                                                        <Stack direction={"row"} gap={0.75}>
                                                                            <IconSchema
                                                                                size={16}
                                                                                htmlColor={
                                                                                    customMetadata
                                                                                        ? theme.palette.success.main
                                                                                        : theme.palette
                                                                                              .greySecondary[500]
                                                                                }
                                                                            />
                                                                            <Typography
                                                                                variant={"body3_400"}
                                                                                color={
                                                                                    customMetadata
                                                                                        ? theme.palette.greySecondary
                                                                                              .main
                                                                                        : theme.palette
                                                                                              .greySecondary[600]
                                                                                }
                                                                            >
                                                                                {customMetadata
                                                                                    ? customMetadata?.label
                                                                                    : t(
                                                                                          `common::label::Choose one custom data.`,
                                                                                      )}
                                                                            </Typography>
                                                                        </Stack>
                                                                    </Grid>
                                                                    <Grid item>
                                                                        <Button
                                                                            disabled={!customMetadata}
                                                                            size={"small"}
                                                                            variant={"text"}
                                                                            startIcon={<IconResetSmall size={16} />}
                                                                            color={"greySecondary"}
                                                                            onClick={handleClearCheckAll}
                                                                        >
                                                                            <Typography variant={"body1_400"}>
                                                                                {t(`common::label::Clear`)}
                                                                            </Typography>
                                                                        </Button>
                                                                    </Grid>
                                                                    <Divider
                                                                        orientation={"vertical"}
                                                                        flexItem
                                                                        sx={{ mx: 1.5 }}
                                                                    />
                                                                    <Grid item>
                                                                        <Button
                                                                            animation={"wave"}
                                                                            animationtarget="startIcon"
                                                                            animationtype="toRight"
                                                                            color="greySecondary"
                                                                            startIcon={<IconSchema size={20} />}
                                                                            endIcon={<IconOpenInNew size={16} />}
                                                                            label="Button"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                openDialog("selectCustomMetadata");
                                                                            }}
                                                                            size="small"
                                                                            type="button"
                                                                            variant="outlined"
                                                                        >
                                                                            {t(
                                                                                `common::label::Custom Metadata`,
                                                                                `Custom Metadata`,
                                                                            )}
                                                                        </Button>
                                                                    </Grid>
                                                                </Grid>
                                                            </Stack>
                                                            {filteredCustomMetadata.length > 0 && (
                                                                <>
                                                                    <Divider sx={{ mt: 2.5, mb: 2.5 }} />
                                                                    <Grid container gap={2.5}>
                                                                        {filteredCustomMetadata?.map(
                                                                            (
                                                                                {
                                                                                    id,
                                                                                    subtitle,
                                                                                    type,
                                                                                    options,
                                                                                    value,
                                                                                    description,
                                                                                },
                                                                                index,
                                                                            ) => (
                                                                                <Grid
                                                                                    key={index}
                                                                                    container
                                                                                    flexDirection={"row"}
                                                                                    gap={2.5}
                                                                                    alignItems={"center"}
                                                                                >
                                                                                    <Grid item sx={{ width: "160px" }}>
                                                                                        <PanelPropertyTitle
                                                                                            variant={"body1_500"}
                                                                                            subtitle={subtitle}
                                                                                            tooltip={description}
                                                                                        />
                                                                                    </Grid>
                                                                                    <Grid
                                                                                        item
                                                                                        flex={1}
                                                                                        sx={{ width: "100%" }}
                                                                                    >
                                                                                        <MetadataFieldEditMode
                                                                                            id={id}
                                                                                            type={type}
                                                                                            value={value}
                                                                                            options={options?.map(
                                                                                                (option) => ({
                                                                                                    ...option,
                                                                                                    selected: values[
                                                                                                        id
                                                                                                    ]?.includes(
                                                                                                        option.value,
                                                                                                    ),
                                                                                                }),
                                                                                            )}
                                                                                            onChangeField={(
                                                                                                id,
                                                                                                value,
                                                                                            ) => {
                                                                                                handleChangeMetadata(
                                                                                                    id,
                                                                                                    value,
                                                                                                );
                                                                                            }}
                                                                                            showValidationError={false}
                                                                                        />
                                                                                    </Grid>
                                                                                </Grid>
                                                                            ),
                                                                        )}
                                                                    </Grid>
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                <VersionedComponent version={VERSION_CM_13540_ASSET_SEARCH_BAR_V2}>
                                                    <Divider sx={{ mt: 2.5, mb: 2.5 }} />
                                                    <Stack>
                                                        <Grid container alignItems={"center"}>
                                                            <Grid item xs={3}>
                                                                <Typography variant={"body1_400"}>
                                                                    {t(`common::label::Starred`)}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs>
                                                                <Checkbox
                                                                    color={"greySecondary"}
                                                                    name="favoritesBy"
                                                                    checked={!!values.favoritesBy}
                                                                    onChange={(e) => {
                                                                        handleChangeValue(
                                                                            "favoritesBy",
                                                                            e.target.checked ? stage.user?.id : null,
                                                                        );
                                                                    }}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </Stack>
                                                </VersionedComponent>
                                                {/*File Meta 영역*/}
                                                {/*<VersionedComponent*/}
                                                {/*    name={"AssetListSearchBar"}*/}
                                                {/*    version={VERSION_CM_4923_FILE_META_SEARCH}*/}
                                                {/*>*/}
                                                {/*    <Stack>*/}
                                                {/*        <Grid container alignItems={"center"}>*/}
                                                {/*            <Grid*/}
                                                {/*                item*/}
                                                {/*                xs={*/}
                                                {/*                    values.fileMetaTemp &&*/}
                                                {/*                    values.fileMetaTemp.length !== 0*/}
                                                {/*                        ? 12*/}
                                                {/*                        : 3*/}
                                                {/*                }*/}
                                                {/*            >*/}
                                                {/*                <Typography variant={"body1_400"}>*/}
                                                {/*                    {i18n.t(`common::label::File meta`)}*/}
                                                {/*                </Typography>*/}
                                                {/*            </Grid>*/}
                                                {/*            <Grid*/}
                                                {/*                item*/}
                                                {/*                xs*/}
                                                {/*                mt={*/}
                                                {/*                    values.fileMetaTemp &&*/}
                                                {/*                    values.fileMetaTemp.length !== 0*/}
                                                {/*                        ? 2*/}
                                                {/*                        : 0*/}
                                                {/*                }*/}
                                                {/*            >*/}
                                                {/*                <Attributions*/}
                                                {/*                    attributions={values.fileMetaTemp || []}*/}
                                                {/*                    keyOptions={fileMetaKeyOptions}*/}
                                                {/*                    onFileMetaUpdated={(fileMeta) => {*/}
                                                {/*                        const fileMetaParam =*/}
                                                {/*                            fileMeta &&*/}
                                                {/*                            fileMeta.organizedItems &&*/}
                                                {/*                            fileMeta.organizedItems.length > 0*/}
                                                {/*                                ? JSON.stringify({*/}
                                                {/*                                      items: fileMeta.organizedItems,*/}
                                                {/*                                  })*/}
                                                {/*                                : undefined;*/}
                                                {/*                        handleChangeValue(*/}
                                                {/*                            "fileMetaTemp",*/}
                                                {/*                            fileMeta.tempItems,*/}
                                                {/*                        );*/}
                                                {/*                        handleChangeValue("fileMeta", fileMetaParam);*/}
                                                {/*                    }}*/}
                                                {/*                />*/}
                                                {/*            </Grid>*/}
                                                {/*        </Grid>*/}
                                                {/*    </Stack>*/}
                                                {/*</VersionedComponent>*/}
                                            </Box>
                                            <Divider sx={{ mt: 3, mb: 3 }} />
                                            <Grid container alignItems={"center"} justifyContent={"space-between"}>
                                                <Grid item>
                                                    {values.selectedCategoryId &&
                                                        values.selectedCategoryId !== UNCATEGORIZED_ID &&
                                                        !values.categoryGroupId && (
                                                            <VersionedComponent
                                                                version={VERSION_CM_6575_ASSET_CATEGORY_GROUP_SEARCH}
                                                            >
                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox
                                                                            color={"greySecondary"}
                                                                            name="isIncludeChildCategory"
                                                                            checked={
                                                                                values.isIncludeChildCategory || false
                                                                            }
                                                                            onChange={(e) => {
                                                                                handleChangeValue(
                                                                                    "isIncludeChildCategory",
                                                                                    e.target.checked,
                                                                                );
                                                                            }}
                                                                        />
                                                                    }
                                                                    label={i18n.t(
                                                                        `common::label::Search with subcategories`,
                                                                    )}
                                                                />
                                                            </VersionedComponent>
                                                        )}
                                                </Grid>
                                                <Grid item>
                                                    <Button
                                                        variant={"text"}
                                                        size={"medium"}
                                                        color={"greySecondary"}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            resetForm();
                                                            if (onClickReset) onHandleClickReset(e);
                                                        }}
                                                    >
                                                        {i18n.t(`common::label::Reset`)}
                                                    </Button>
                                                    <Button
                                                        variant={"contained"}
                                                        color={"primary"}
                                                        type={"submit"}
                                                        size={"medium"}
                                                        sx={{ ml: 0.75 }}
                                                        onClick={() => handleSubmit()}
                                                    >
                                                        {i18n.t(`common::label::Search`)}
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item>
                                            <Tooltip title={t(`common::label::Close`, `Close`)}>
                                                <IconButton
                                                    type={"button"}
                                                    size={"medium"}
                                                    circled
                                                    onClick={() => {
                                                        if (onChangeQuickSearch) onChangeQuickSearch();
                                                    }}
                                                >
                                                    <IconClose size={16} color={"greySecondary"} />
                                                </IconButton>
                                            </Tooltip>
                                        </Grid>
                                    </Grid>
                                </Popover>
                            </Grid>
                            <VersionedComponent version={VERSION_CM_12923_LIST_SEARCHBAR_FILTER}>
                                <Grid item flex={1} sx={{ display: "flex", ml: 1.5, alignItems: "center" }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                color="greySecondary"
                                                name="favoritesBy"
                                                checked={!!initialValues?.favoritesBy}
                                                onChange={(e) => {
                                                    handleChangeFilterValue({
                                                        favoritesBy: e.target.checked ? stage.user?.id : null,
                                                    });
                                                }}
                                                sx={{ mr: 1 }}
                                            />
                                        }
                                        label={
                                            <Typography
                                                variant="body3_400"
                                                color={theme.palette.greySecondary.main}
                                                noWrap
                                            >
                                                {t(`common::label::Starred`)}
                                            </Typography>
                                        }
                                    />
                                    <Divider orientation="vertical" flexItem sx={{ my: theme.spacing(1), mr: 1.5 }} />
                                    {!!hiddenSearchFilterParams && (
                                        <ResponsiveSearchFilters
                                            domain={domain}
                                            hiddenParams={hiddenSearchFilterParams}
                                            type={SEARCH_FILTER_TYPE.SEARCH_QUERY_PARAMS}
                                            optionValues={optionValues}
                                            values={initialValues}
                                            onClickSubmit={handleChangeFilterValue}
                                            onClickReset={onHandleClickReset}
                                            isSearchEngine={isSearchEngine}
                                            scope={projectId ? SPACE_SCOPE.PROJECT : SPACE_SCOPE.ADMIN}
                                            currentCategoryGroup={currentCategoryGroup}
                                        />
                                    )}
                                </Grid>
                            </VersionedComponent>
                        </Grid>
                    </form>
                )}
            </Formik>
        </>
    );
};
AssetSearchBar.propTypes = {
    initialValues: PropTypes.object,
    validationSchema: PropTypes.object,
    isQuickSearch: PropTypes.bool,
    onClickCancel: PropTypes.func,
    onClickReset: PropTypes.func,
    onChangeQuickSearch: PropTypes.func,
    aiModeSlot: PropTypes.node,
    aiSearchEnabled: PropTypes.bool,
};

export default AssetSearchBar;
